<template>
  <div id="suggestions-container">
    <ul class="suggestion-options">
      <li v-if="citySuggestionsLength" class="suggestion-header">Cities</li>
      <li v-for="suggestion in modifiedCitySuggestions" v-bind:key="suggestion.id" class="suggestion-option" v-on:click="$emit('suggestionSearch', suggestion)">{{ suggestion.displayText }}</li>
      <hr v-if="citySuggestionsLength && neighborhoodSuggestionsLength" class="suggestion-divider">
      <li v-if="neighborhoodSuggestionsLength" class="suggestion-header">Neighborhoods</li>
      <li v-for="suggestion in modifiedNeighborhoodSuggestions" v-bind:key="suggestion.id" class="suggestion-option" v-on:click="$emit('suggestionSearch', suggestion)">{{ suggestion.displayText }}</li>
      <hr v-if="(citySuggestionsLength || neighborhoodSuggestions) && zipSuggestionsLength" class="suggestion-divider">
      <li v-if="zipSuggestionsLength" class="suggestion-header">Zip Codes</li>
      <li v-for="suggestion in zipSuggestions" v-bind:key="suggestion.id" class="suggestion-option" v-on:click="$emit('suggestionSearch', suggestion)">{{ suggestion.displayText }}</li>
      <hr v-if="(citySuggestionsLength || zipSuggestionsLength) && addressSuggestionsLength" class="suggestion-divider">
      <li v-if="addressSuggestionsLength" class="suggestion-header">Addresses</li>
      <li v-for="suggestion in modifiedAddressSuggestions" v-bind:key="suggestion.id" class="suggestion-option" v-on:click="handleAddressSearch(suggestion)">{{ suggestion.displayText }}</li>
      <li v-if="addressSuggestionsLength" v-on:click="$refs.customHomeConfirmationModal.show()" class="suggestion-option missing-home">Don't See Your Home?</li>
    </ul>
    <b-modal ref="customHomeConfirmationModal" size="lg" hide-footer centered>
      <template v-slot:modal-title>
        <h5>Want to Start Tracking Your HomFAX?</h5>
      </template>
      <p>HomApt gets housing data from MLS. If you do not see your home yet, that means your house has not been bought or sold since HomFAX started. Good news, you can add it now!<br/>
         <br/>
         What happens when you add your house?<br/>
         ~ Access to store receipts and create HomFAXs about your house that only you can see<br/>
         ~ No one else can claim your house or see your owner information<br/>
         ~ Update your HomFAX to add value to your home<br/>
         ~ Get valuable tax documents when you sell your house<br/>
         <br/>
         According to our <router-link to="/terms">Terms of Use</router-link>, you can only claim houses you own.
      </p>
      <div class="container">
        <div class="row">
          <div class="col-4 offset-2">
            <router-link to="/property/new">
              <button class="btn btn-primary">Yes</button>
            </router-link>
          </div>
          <div class="col-4">
            <button class="btn btn-secondary" v-on:click="$refs.customHomeConfirmationModal.hide()">Close</button>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import AWS from 'aws-sdk/global'
import AWSConnector from 'http-aws-es'
import elasticsearch from 'elasticsearch'
import router from '../../../../router/index.js'

export default {
  data () {
    return {
      addressSuggestions: [],
      citySuggestions: [],
      neighborhoodSuggestions: [],
      zipSuggestions: [],
      wordSuggestionNumberSplit: 5,
      maximumNumberSuggestions: 10
    }
  },
  computed: {
    ...mapGetters([
      'getSecretAccessKey',
      'getAccessKeyId',
      'getSessionToken'
    ]),
    addressSuggestionsLength () {
      return this.modifiedAddressSuggestions.length
    },
    modifiedAddressSuggestions () {
      let numberOfSuggestions = this.wordSuggestionsLength > this.zipSuggestionsLength ? this.wordSuggestionsLength : this.zipSuggestionsLength
      return this.addressSuggestions.slice(0, this.maximumNumberSuggestions - Math.min(numberOfSuggestions, this.maximumNumberSuggestions))
    },
    citySuggestionsLength () {
      return this.citySuggestions.length
    },
    modifiedCitySuggestions () {
      if (this.neighborhoodSuggestionsLength >= 5) {
        return this.citySuggestions.slice(0, this.maximumNumberSuggestions - Math.min(this.wordSuggestionNumberSplit, this.citySuggestionsLength))
      }
      return this.citySuggestions.slice(0, this.maximumNumberSuggestions - this.neighborhoodSuggestionsLength)
    },
    neighborhoodSuggestionsLength () {
      return this.neighborhoodSuggestions.length
    },
    modifiedNeighborhoodSuggestions () {
      if (this.citySuggestionsLength >= 5) {
        return this.neighborhoodSuggestions.slice(0, this.maximumNumberSuggestions - Math.min(this.wordSuggestionNumberSplit, this.neighborhoodSuggestionsLength))
      }
      return this.neighborhoodSuggestions.slice(0, this.maximumNumberSuggestions - this.citySuggestionsLength)
    },
    wordSuggestionsLength () {
      return this.modifiedCitySuggestions.length + this.modifiedNeighborhoodSuggestions.length
    },
    zipSuggestionsLength () {
      return this.zipSuggestions.length
    }
  },
  methods: {
    ...mapActions([
      'unauthenticatedLogin'
    ]),
    clearSuggestions () {
      this.addressSuggestions = []
      this.citySuggestions = []
      this.neighborhoodSuggestions = []
      this.zipSuggestions = []
    },
    handleAddressSearch (suggestion) {
      // Handle case where this is a multi-unit building
      if ('unit_number' in suggestion) {
        this.$emit('suggestionSearch', suggestion)
        return
      }

      // Navigate to property page
      let address = ''
      if ('street_name' in suggestion) { address += suggestion['street_name'] }
      if ('city' in suggestion) { address += ' ' + suggestion['city'] }
      router.push('/propertysearch/' + suggestion.house_id + '/' + address + '/info')
    },
    async retrieveAddressSuggestions (searchString) {
      try {
        if (!this.getSecretAccessKey) await this.unauthenticatedLogin()
        AWS.config.update({
          credentials: new AWS.Credentials(this.getAccessKeyId, this.getSecretAccessKey, this.getSessionToken),
          region: window.CONFIG.Region
        })
        var client = new elasticsearch.Client({
          connectionClass: AWSConnector,
          host: window.CONFIG.SearchDomain,
          apiVersion: '6.4'
        })
        const query = {
          'suggest': {
            'address_suggestions': {
              'prefix': searchString,
              'completion': {
                'field': 'address_suggest',
                'fuzzy': {'fuzziness': 1},
                'size': this.maximumNumberSuggestions
              }
            }
          }
        }
        const results = await client.search({
          index: 'house_suggestion',
          type: '_doc',
          body: query
        })
        this.addressSuggestions = results.suggest.address_suggestions[0].options.map(s => ({
          'displayText': this.createAddressDisplayText(s['_source']),
          ...('house_id' in s['_source'] && {'house_id': s['_source']['house_id']}),
          ...('unit_number' in s['_source'] && {'unit_number': s['_source']['unit_number']}),
          ...('street_number' in s['_source'] && {'street_number': s['_source']['street_number']}),
          ...('street_name' in s['_source'] && {'street_name': s['_source']['street_name']}),
          ...('street_suffix' in s['_source'] && {'street_suffix': s['_source']['street_suffix']}),
          ...('street_dir' in s['_source'] && {'street_dir': s['_source']['street_dir']}),
          ...('city' in s['_source'] && {'city': s['_source']['city']}),
          ...('state' in s['_source'] && {'state': s['_source']['state']}),
          ...('zip_code' in s['_source'] && {'zip_code': s['_source']['zip_code']})
        }))
      } catch (error) {
        console.log(error)
      }
    },
    createAddressDisplayText (addressObj) {
      let address = ''
      if ('street_number' in addressObj) { address += addressObj['street_number'] + ' ' }
      if ('street_name' in addressObj) { address += addressObj['street_name'] }
      if ('street_suffix' in addressObj) { address += ' ' + addressObj['street_suffix'] }
      if ('street_dir' in addressObj) { address += ' ' + addressObj['street_dir'] }
      if ('city' in addressObj) { address += ', ' + addressObj['city'] }
      if ('state' in addressObj) { address += ', ' + addressObj['state'] }
      if ('zip_code' in addressObj) { address += ', ' + addressObj['zip_code'] }

      return address
    },
    async retrieveCitySuggestions (searchString) {
      try {
        if (!this.getSecretAccessKey) await this.unauthenticatedLogin()
        AWS.config.update({
          credentials: new AWS.Credentials(this.getAccessKeyId, this.getSecretAccessKey, this.getSessionToken),
          region: window.CONFIG.Region
        })
        var client = new elasticsearch.Client({
          connectionClass: AWSConnector,
          host: window.CONFIG.SearchDomain,
          apiVersion: '6.4'
        })
        const query = {
          'suggest': {
            'city_suggestions': {
              'prefix': searchString,
              'completion': {
                'field': 'city_suggest',
                'fuzzy': {'fuzziness': 1},
                'size': this.maximumNumberSuggestions
              }
            }
          }
        }
        const results = await client.search({
          index: 'house_suggestion',
          type: '_doc',
          body: query
        })
        this.citySuggestions = results.suggest.city_suggestions[0].options.map(s => ({'displayText': s['_source']['city'] + ', ' + s['_source']['state'], 'city': s['_source']['city'], 'state': s['_source']['state']}))
      } catch (error) {
        console.log(error)
      }
    },
    async retrieveNeighborhoodSuggestions (searchString) {
      try {
        if (!this.getSecretAccessKey) await this.unauthenticatedLogin()
        AWS.config.update({
          credentials: new AWS.Credentials(this.getAccessKeyId, this.getSecretAccessKey, this.getSessionToken),
          region: window.CONFIG.Region
        })
        var client = new elasticsearch.Client({
          connectionClass: AWSConnector,
          host: window.CONFIG.SearchDomain,
          apiVersion: '6.4'
        })
        const query = {
          'suggest': {
            'neighborhood_suggestions': {
              'prefix': searchString,
              'completion': {
                'field': 'neighborhood_suggest',
                'fuzzy': {'fuzziness': 1},
                'size': this.maximumNumberSuggestions
              }
            }
          }
        }
        const results = await client.search({
          index: 'house_suggestion',
          type: '_doc',
          body: query
        })
        this.neighborhoodSuggestions = results.suggest.neighborhood_suggestions[0].options.map(s => ({'displayText': s['_source']['neighborhood'] + ' ' + s['_source']['city'] + ', ' + s['_source']['state'], 'neighborhood': s['_source']['neighborhood'], 'city': s['_source']['city'], 'state': s['_source']['state']}))
      } catch (error) {
        console.log(error)
      }
    },
    async retrieveZipCodeSuggestions (searchString) {
      try {
        if (!this.getSecretAccessKey) await this.unauthenticatedLogin()
        AWS.config.update({
          credentials: new AWS.Credentials(this.getAccessKeyId, this.getSecretAccessKey, this.getSessionToken),
          region: window.CONFIG.Region
        })
        var client = new elasticsearch.Client({
          connectionClass: AWSConnector,
          host: window.CONFIG.SearchDomain,
          apiVersion: '6.4'
        })
        const query = {
          'suggest': {
            'zip_code_suggestions': {
              'prefix': searchString,
              'completion': {
                'field': 'zip_code_suggest',
                'fuzzy': {'fuzziness': 1},
                'size': this.maximumNumberSuggestions
              }
            }
          }
        }
        const results = await client.search({
          index: 'house_suggestion',
          type: '_doc',
          body: query
        })
        this.zipSuggestions = results.suggest.zip_code_suggestions[0].options.map(s => ({'displayText': s['_source']['zip_code'] + ', ' + s['_source']['state'], 'zip_code': s['_source']['zip_code'], 'state': s['_source']['state']}))
      } catch (error) {
        console.log(error)
      }
    },
    async triggerSuggestions (searchString) {
      if (searchString.length > 2 && searchString.match(/^[0-9]+$/)) {
        this.retrieveZipCodeSuggestions(searchString)
        this.citySuggestions = []
      } else if (searchString.length > 2) {
        this.retrieveCitySuggestions(searchString)
        this.retrieveNeighborhoodSuggestions(searchString)
        this.zipSuggestions = []
      }
      if (searchString.length > 2) {
        this.retrieveAddressSuggestions(searchString)
      }
    }
  }
}
</script>

<style scoped>
.btn {
  width: 100%;
}

.missing-home {
  padding-left: 0.5rem !important;
}

.suggestion-options {
  position: absolute;
  width: 75%;
  background-color: rgba(255, 255, 255, 0.95);
  list-style-type: none;
  padding: 0;
}

.suggestion-option {
  padding-left: 1rem;
}

.suggestion-option:hover {
  background-color: rgb(254, 237, 170);
  cursor: pointer;
}

.suggestion-header {
  padding-left: 0.5rem;
  color: #6c757d;
  font-size: .875rem;
}

.suggestion-divider {
  margin: 0.5rem;
}

#suggestions-container {
  position: relative;
  width: 100%;
  margin-right: .5rem;
  margin-left: .5rem;
  display: block;
  z-index: 2;
}
</style>



// WEBPACK FOOTER //
// src/components/home/homeSearch/searchBar/HouseSuggestionDropdown.vue